import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import * as chrono from "chrono-node";
import { editorSchema } from "@/lib/editorSchema";
import { createItemId } from "@/lib/utils";
import type { Node as ProseMirrorNode } from "prosemirror-model";

const STORAGE_KEY = "timeline-integrated-editor";
const AUTO_SAVE_DELAY = 1500;
const PARSE_DELAY = 300;

export interface ListItem {
  id: string;
  text: string;
  level: number;
  parentId: string | null;
  extractedStartDate: Date | null;
  extractedEndDate: Date | null;
  manualStartDate: Date | null;
  manualEndDate: Date | null;
  children: ListItem[];
}

export interface TimelineEvent {
  id: string;
  itemId: string;
  text: string;
  startDate: Date;
  endDate: Date | null;
  isManual: boolean;
  hasChildren: boolean;
  childCount: number;
  level: number;
  expanded: boolean;
  parentId: string | null;
}

interface ManualOverride {
  start?: string;
  end?: string | null;
  date?: string; // legacy single-date overrides
  originalText: string;
}

interface WorkspaceState {
  id: string;
  name: string;
  editorContent: any;
  manualDateOverrides: Record<string, ManualOverride>;
  lastModified: number;
  createdAt: number;
}

interface EditorContextValue {
  doc: any;
  setDocContent: (doc: any) => void;
  listItems: ListItem[];
  flattenedListItems: ListItem[];
  listItemMap: Map<string, ListItem>;
  timelineEvents: TimelineEvent[];
  selectedItemId: string | null;
  selectItem: (itemId: string | null) => void;
  manualOverrides: Record<string, ManualOverride>;
  updateManualDate: (itemId: string, start: Date, text: string, end?: Date | null) => void;
  resetManualDate: (itemId: string) => void;
  expandedItemIds: Set<string>;
  toggleExpanded: (itemId: string) => void;
  modalItemId: string | null;
  modalItem?: ListItem;
  openDateEditor: (itemId: string) => void;
  closeDateEditor: () => void;
  lastModified: number;
  workspaceName: string;
  workspaces: WorkspaceState[];
  activeWorkspaceId: string | null;
  selectWorkspace: (workspaceId: string) => void;
  createWorkspace: (name?: string) => string | null;
  renameWorkspace: (workspaceId: string, nextName: string) => void;
  deleteWorkspace: (workspaceId: string) => boolean;
}

const EditorContext = createContext<EditorContextValue | undefined>(undefined);

interface ParsedChronoRange {
  start: Date | null;
  end: Date | null;
}

const RANGE_FALLBACK_REGEX = /(.+?)\s*(?:\bto\b|\bthrough\b|\bthru\b|\buntil\b|\btill\b|[-–—~]|->|→)\s+(.+)/i;
const EMPTY_OVERRIDES: Record<string, ManualOverride> = {};
const WORKSPACE_BASE_NAME = "Workspace";

function parseLooseRange(text: string): ParsedChronoRange {
  const match = RANGE_FALLBACK_REGEX.exec(text);
  if (!match) {
    return { start: null, end: null };
  }

  const rawStart = match[1];
  const rawEnd = match[2];
  if (!rawStart || !rawEnd) {
    return { start: null, end: null };
  }

  const start = chrono.parseDate(rawStart.trim());
  const end = chrono.parseDate(rawEnd.trim());

  if (start && end) {
    return normalizeRange({ start, end });
  }

  return { start, end: null };
}

function normalizeRange(range: ParsedChronoRange): ParsedChronoRange {
  const { start, end } = range;
  if (start && end && end.getTime() < start.getTime()) {
    return { start: end, end: start };
  }
  return range;
}

function parseChronoRange(text: string): ParsedChronoRange {
  const parsed = chrono.parse(text);
  if (!parsed.length) {
    return parseLooseRange(text);
  }

  const primary = parsed[0];
  const rest = parsed.slice(1);
  if (!primary) {
    return parseLooseRange(text);
  }
  const start = primary.start?.date() ?? null;
  if (!start) {
    return parseLooseRange(text);
  }

  if (primary.end) {
    return normalizeRange({ start, end: primary.end.date() ?? null });
  }

  const sequential = rest.find(result => Boolean(result.start));
  if (sequential?.start) {
    return normalizeRange({ start, end: sequential.start.date() });
  }

  const fallback = parseLooseRange(text);
  if (fallback.start && fallback.end) {
    return fallback;
  }

  return { start, end: null };
}

function createDefaultDoc() {
  return {
    type: "doc",
    content: [
      {
        type: "ordered_list",
        attrs: { order: 1 },
        content: [
          {
            type: "list_item",
            attrs: { id: createItemId() },
            content: [
              {
                type: "paragraph",
              },
            ],
          },
        ],
      },
    ],
  };
}

const createWorkspaceId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `ws_${Math.random().toString(36).slice(2, 10)}`;
};

function buildWorkspace(partial?: Partial<WorkspaceState>): WorkspaceState {
  const now = Date.now();
  return {
    id: partial?.id ?? createWorkspaceId(),
    name: partial?.name ?? `${WORKSPACE_BASE_NAME} ${now.toString(36).slice(-2)}`,
    editorContent: partial?.editorContent ?? createDefaultDoc(),
    manualDateOverrides: partial?.manualDateOverrides ? { ...partial.manualDateOverrides } : {},
    lastModified: partial?.lastModified ?? now,
    createdAt: partial?.createdAt ?? now,
  };
}

const normalizeStoredWorkspace = (stored: any, fallbackName: string): WorkspaceState =>
  buildWorkspace({
    id: typeof stored?.id === "string" ? stored.id : undefined,
    name: typeof stored?.name === "string" && stored.name.trim().length ? stored.name : fallbackName,
    editorContent: stored?.editorContent ?? createDefaultDoc(),
    manualDateOverrides: stored?.manualDateOverrides ?? {},
    lastModified: typeof stored?.lastModified === "number" ? stored.lastModified : Date.now(),
    createdAt: typeof stored?.createdAt === "number" ? stored.createdAt : Date.now(),
  });

function getNextWorkspaceName(workspaces: WorkspaceState[]) {
  const numericSuffixes = workspaces
    .map(ws => {
      const match = ws.name.match(/^Workspace\s+(\d+)$/i);
      return match ? Number(match[1]) : null;
    })
    .filter((value): value is number => value !== null);
  const nextIndex = numericSuffixes.length ? Math.max(...numericSuffixes) + 1 : workspaces.length + 1;
  return `${WORKSPACE_BASE_NAME} ${nextIndex}`;
}

function walkListItems(
  node: ProseMirrorNode,
  parentId: string | null,
  level: number,
  overrides: Record<string, ManualOverride>
): ListItem[] {
  const items: ListItem[] = [];

  node.forEach(child => {
    if (child.type.name === "doc" || child.type.name === "ordered_list") {
      items.push(...walkListItems(child, parentId, level, overrides));
      return;
    }

    if (child.type.name !== "list_item") {
      return;
    }

    const id = child.attrs.id || createItemId();
    let text = "";
    let nestedList: ProseMirrorNode | null = null;

    child.forEach(grandchild => {
      if (grandchild.type.name === "paragraph") {
        text = grandchild.textContent;
      } else if (grandchild.type.name === "ordered_list") {
        nestedList = grandchild;
      }
    });

    const { start: extractedStartDate, end: extractedEndDate } = parseChronoRange(text);
    const override = overrides[id];
    const manualStartIso = override?.start ?? override?.date;
    const manualEndIso = override?.end ?? null;
    const manualStartDate = manualStartIso ? new Date(manualStartIso) : null;
    const manualEndDate = manualEndIso ? new Date(manualEndIso) : null;
    const sanitizedText = text.trim();
    const children = nestedList ? walkListItems(nestedList, id, level + 1, overrides) : [];

    items.push({
      id,
      text: sanitizedText,
      level,
      parentId,
      extractedStartDate,
      extractedEndDate,
      manualStartDate,
      manualEndDate,
      children,
    });
  });

  return items;
}

function flattenList(items: ListItem[]) {
  const collection: ListItem[] = [];
  const visit = (item: ListItem) => {
    collection.push(item);
    item.children.forEach(visit);
  };
  items.forEach(visit);
  return collection;
}

function countDescendants(item: ListItem): number {
  return item.children.reduce((acc, child) => acc + 1 + countDescendants(child), 0);
}

function buildTimelineEvents(items: ListItem[], expanded: Set<string>): TimelineEvent[] {
  const flat = flattenList(items);
  const itemMap = new Map(flat.map(item => [item.id, item]));

  const shouldBeVisible = (item: ListItem) => {
    if (item.level === 0) return true;
    let currentParentId = item.parentId;
    while (currentParentId) {
      const parent = itemMap.get(currentParentId);
      if (!parent) break;
      if (!expanded.has(parent.id)) {
        return false;
      }
      currentParentId = parent.parentId;
    }
    return true;
  };

  return flat
    .filter(item => shouldBeVisible(item))
    .map(item => {
      const startDate = item.manualStartDate ?? item.extractedStartDate;
      if (!startDate) return null;
      let endDate: Date | null = null;
      if (item.manualStartDate) {
        if (item.manualEndDate && item.manualEndDate.getTime() >= item.manualStartDate.getTime()) {
          endDate = item.manualEndDate;
        }
      } else if (item.extractedEndDate && item.extractedEndDate.getTime() >= startDate.getTime()) {
        endDate = item.extractedEndDate;
      }
      return {
        id: item.id,
        itemId: item.id,
        text: item.text || "Untitled item",
        startDate,
        endDate,
        isManual: Boolean(item.manualStartDate),
        hasChildren: item.children.length > 0,
        childCount: countDescendants(item),
        level: item.level,
        expanded: expanded.has(item.id),
        parentId: item.parentId,
      };
    })
    .filter((event): event is TimelineEvent => Boolean(event));
}

export function EditorProvider({ children }: { children: ReactNode }) {
  const [workspaces, setWorkspaces] = useState<WorkspaceState[]>([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [expandedItemIds, setExpandedItemIds] = useState<Set<string>>(new Set());
  const [modalItemId, setModalItemId] = useState<string | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const [debouncedDoc, setDebouncedDoc] = useState(createDefaultDoc());

  const fallbackDoc = useMemo(() => createDefaultDoc(), []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed?.workspaces) && parsed.workspaces.length) {
          const normalized: WorkspaceState[] = parsed.workspaces.map((workspace: any, index: number) =>
            normalizeStoredWorkspace(workspace, `${WORKSPACE_BASE_NAME} ${index + 1}`)
          );
          if (!normalized.length) {
            return;
          }
          const preferredActive = normalized.find(ws => ws.id === parsed.activeWorkspaceId);
          const nextActive = preferredActive ?? normalized[0];
          if (!nextActive) {
            return;
          }
          setWorkspaces(normalized);
          setActiveWorkspaceId(nextActive.id);
          setDebouncedDoc(nextActive.editorContent);
          setIsHydrated(true);
          return;
        }

        if (parsed?.editorContent) {
          const migrated = buildWorkspace({
            name: `${WORKSPACE_BASE_NAME} 1`,
            editorContent: parsed.editorContent,
            manualDateOverrides: parsed.manualDateOverrides ?? {},
            lastModified: parsed.lastModified ?? Date.now(),
          });
          setWorkspaces([migrated]);
          setActiveWorkspaceId(migrated.id);
          setDebouncedDoc(migrated.editorContent);
          setIsHydrated(true);
          return;
        }
      } catch {
        // ignore corrupted storage
      }
    }

    const defaultWorkspace = buildWorkspace({ name: `${WORKSPACE_BASE_NAME} 1` });
    setWorkspaces([defaultWorkspace]);
    setActiveWorkspaceId(defaultWorkspace.id);
    setDebouncedDoc(defaultWorkspace.editorContent);
    setIsHydrated(true);
  }, []);

  const activeWorkspace = useMemo(() => {
    if (!activeWorkspaceId) return null;
    return workspaces.find(ws => ws.id === activeWorkspaceId) ?? null;
  }, [activeWorkspaceId, workspaces]);

  const doc = activeWorkspace?.editorContent ?? fallbackDoc;
  const manualOverrides = activeWorkspace?.manualDateOverrides ?? EMPTY_OVERRIDES;
  const lastModified = activeWorkspace?.lastModified ?? 0;
  const workspaceName = activeWorkspace?.name ?? `${WORKSPACE_BASE_NAME} 1`;

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedDoc(doc);
    }, PARSE_DELAY);
    return () => clearTimeout(handle);
  }, [doc]);

  useEffect(() => {
    if (!isHydrated) return;
    const handler = setTimeout(() => {
      if (typeof window === "undefined") return;
      const payload = {
        workspaces,
        activeWorkspaceId,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    }, AUTO_SAVE_DELAY);
    return () => clearTimeout(handler);
  }, [workspaces, activeWorkspaceId, isHydrated]);

  useEffect(() => {
    setSelectedItemId(null);
    setExpandedItemIds(new Set());
    setModalItemId(null);
  }, [activeWorkspaceId]);

  const listItems = useMemo(() => {
    try {
      const pmDoc = editorSchema.nodeFromJSON(debouncedDoc);
      return walkListItems(pmDoc, null, 0, manualOverrides);
    } catch (error) {
      console.error("Failed to parse editor document", error);
      return [];
    }
  }, [debouncedDoc, manualOverrides]);

  const flattened = useMemo(() => flattenList(listItems), [listItems]);
  const listItemMap = useMemo(() => {
    const map = new Map<string, ListItem>();
    flattened.forEach(item => map.set(item.id, item));
    return map;
  }, [flattened]);

  const timelineEvents = useMemo(
    () => buildTimelineEvents(listItems, expandedItemIds),
    [listItems, expandedItemIds]
  );

  const modalItem = useMemo(() => {
    if (!modalItemId) return undefined;
    return listItemMap.get(modalItemId);
  }, [modalItemId, listItemMap]);

  const selectItem = useCallback((itemId: string | null) => {
    setSelectedItemId(itemId);
  }, []);

  const selectWorkspace = useCallback(
    (workspaceId: string) => {
      if (workspaceId === activeWorkspaceId) return;
      setActiveWorkspaceId(workspaceId);
    },
    [activeWorkspaceId]
  );

  const createWorkspace = useCallback((name?: string) => {
    let createdId: string | null = null;
    setWorkspaces(prev => {
      const nextName = name && name.trim().length ? name.trim() : getNextWorkspaceName(prev);
      const workspace = buildWorkspace({ name: nextName });
      createdId = workspace.id;
      return [...prev, workspace];
    });
    if (createdId) {
      setActiveWorkspaceId(createdId);
    }
    return createdId;
  }, []);

  const renameWorkspace = useCallback((workspaceId: string, nextName: string) => {
    const trimmed = nextName.trim();
    if (!trimmed) return;
    setWorkspaces(prev => prev.map(ws => (ws.id === workspaceId ? { ...ws, name: trimmed } : ws)));
  }, []);

  const deleteWorkspace = useCallback(
    (workspaceId: string) => {
      let removed = false;
      let nextActiveId: string | null = activeWorkspaceId;
      setWorkspaces(prev => {
        if (prev.length <= 1) {
          removed = false;
          return prev;
        }
        const filtered = prev.filter(ws => ws.id !== workspaceId);
        if (filtered.length === prev.length) {
          removed = false;
          return prev;
        }
        removed = true;
        if (workspaceId === activeWorkspaceId) {
          nextActiveId = filtered[0]?.id ?? null;
        }
        return filtered;
      });
      if (removed) {
        setActiveWorkspaceId(nextActiveId ?? null);
      }
      return removed;
    },
    [activeWorkspaceId]
  );

  const toggleExpanded = useCallback((itemId: string) => {
    setExpandedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  const updateManualDate = useCallback(
    (itemId: string, start: Date, text: string, end?: Date | null) => {
      if (!activeWorkspaceId) return;
      const normalizedEnd = end && end.getTime() >= start.getTime() ? end : null;
      const timestamp = Date.now();
      setWorkspaces(prev =>
        prev.map(ws => {
          if (ws.id !== activeWorkspaceId) return ws;
          return {
            ...ws,
            manualDateOverrides: {
              ...ws.manualDateOverrides,
              [itemId]: {
                start: start.toISOString(),
                end: normalizedEnd ? normalizedEnd.toISOString() : undefined,
                originalText: text,
              },
            },
            lastModified: timestamp,
          };
        })
      );
    },
    [activeWorkspaceId]
  );

  const resetManualDate = useCallback(
    (itemId: string) => {
      if (!activeWorkspaceId) return;
      const timestamp = Date.now();
      setWorkspaces(prev =>
        prev.map(ws => {
          if (ws.id !== activeWorkspaceId) return ws;
          if (!ws.manualDateOverrides[itemId]) {
            return ws;
          }
          const nextOverrides = { ...ws.manualDateOverrides };
          delete nextOverrides[itemId];
          return {
            ...ws,
            manualDateOverrides: nextOverrides,
            lastModified: timestamp,
          };
        })
      );
    },
    [activeWorkspaceId]
  );

  const openDateEditor = useCallback((itemId: string) => {
    setModalItemId(itemId);
    setSelectedItemId(itemId);
  }, []);

  const closeDateEditor = useCallback(() => {
    setModalItemId(null);
  }, []);

  const setDocContent = useCallback(
    (nextDoc: any) => {
      if (!activeWorkspaceId) return;
      const timestamp = Date.now();
      setWorkspaces(prev =>
        prev.map(ws => (ws.id === activeWorkspaceId ? { ...ws, editorContent: nextDoc, lastModified: timestamp } : ws))
      );
    },
    [activeWorkspaceId]
  );

  const value = useMemo<EditorContextValue>(
    () => ({
      doc,
      setDocContent,
      listItems,
      flattenedListItems: flattened,
      listItemMap,
      timelineEvents,
      selectedItemId,
      selectItem,
      manualOverrides,
      updateManualDate,
      resetManualDate,
      expandedItemIds,
      toggleExpanded,
      modalItemId,
      modalItem,
      openDateEditor,
      closeDateEditor,
      lastModified,
      workspaceName,
      workspaces,
      activeWorkspaceId,
      selectWorkspace,
      createWorkspace,
      renameWorkspace,
      deleteWorkspace,
    }),
    [
      doc,
      flattened,
      listItemMap,
      listItems,
      timelineEvents,
      selectedItemId,
      selectItem,
      manualOverrides,
      updateManualDate,
      resetManualDate,
      expandedItemIds,
      toggleExpanded,
      modalItemId,
      modalItem,
      openDateEditor,
      closeDateEditor,
      lastModified,
      workspaceName,
      workspaces,
      activeWorkspaceId,
      selectWorkspace,
      createWorkspace,
      renameWorkspace,
      deleteWorkspace,
      setDocContent,
    ]
  );

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
}

export function useEditorContext() {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error("useEditorContext must be used within EditorProvider");
  }
  return context;
}
